import { useEffect } from "react";
import { isTopLayer } from "./layer-stack.js";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface UseFocusTrapOptions {
  /** Trap only while active (typically the overlay's `open`). */
  active: boolean;
  /** Ref to focus first; falls back to the first focusable, then container. */
  initialFocusRef?: React.RefObject<HTMLElement | null> | undefined;
  /** Restore focus to the pre-open element on deactivate (default true). */
  returnFocus?: boolean;
  /** Layer-stack id. When supplied, a nested overlay suspends this trap. */
  layerId?: string;
}

/**
 * Focus containment for modal layers (strategy §14.3 `useFocusTrap`).
 * Hardened from RightDrawer's hand-rolled trap — the only one that existed.
 * Tab/Shift+Tab wrap inside the container; focus is restored on close.
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  { active, initialFocusRef, layerId, returnFocus = true }: UseFocusTrapOptions,
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      (document.activeElement as HTMLElement | null) ?? null;

    const focusables = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("aria-hidden"));

    // Initial focus: explicit target → first focusable → the container itself.
    const focusTimer = window.setTimeout(() => {
      if (layerId && !isTopLayer(layerId)) return;
      const target = initialFocusRef?.current ?? focusables()[0] ?? container;
      if (target === container && !container.hasAttribute("tabindex")) {
        container.setAttribute("tabindex", "-1");
      }
      target.focus();
    }, 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (layerId && !isTopLayer(layerId)) return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const current = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (current === first || !container.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last || !container.contains(current)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey);
      if (
        returnFocus &&
        previouslyFocused &&
        typeof previouslyFocused.focus === "function"
      ) {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef, initialFocusRef, layerId, returnFocus]);
}
